import { computed, ref } from 'vue'
import { createGlobalState } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { getEntertainment } from '@/api/game'
import type { EntertainmentDTO, PlatformDTO } from '@/types/game'
import { VENUE_CODE, type VenueCode } from '@/config/venue'

export interface CategoryTabItem {
  label?: string
  labelKey?: string
  value: number
  code: string
  id: number
  platformRespList: (PlatformDTO | undefined)[]
  isIconOnly?: boolean
}

const CATEGORY_ORDER: VenueCode[] = [
  VENUE_CODE.ISLOT,
  VENUE_CODE.LIVE,
  VENUE_CODE.SLOT,
  VENUE_CODE.SPORT,
  VENUE_CODE.ESPORTS,
  VENUE_CODE.MINI,
  VENUE_CODE.FISH
]

const VENUE_CATEGORY_CODES: VenueCode[] = [
  VENUE_CODE.LIVE,
  VENUE_CODE.SLOT,
  VENUE_CODE.SPORT,
  VENUE_CODE.ESPORTS,
  VENUE_CODE.FISH
]

export const useGameCategory = createGlobalState(() => {
  const { t } = useI18n()

  const gameCategoryData = ref<EntertainmentDTO[]>([])
  const categoryLoading = ref(false)
  const categoryHasRequested = ref(false)

  const CATEGORY_CONFIG = computed<Record<VenueCode, CategoryTabItem>>(() => ({
    [VENUE_CODE.ISLOT]: {
      labelKey: t('slot_machine_text'),
      value: 2,
      code: VENUE_CODE.ISLOT,
      id: 2,
      platformRespList: [],
      isIconOnly: false
    },
    [VENUE_CODE.MINI]: {
      labelKey: t('Mini'),
      value: 99,
      code: VENUE_CODE.MINI,
      id: 99,
      platformRespList: []
    },
    [VENUE_CODE.LIVE]: {
      labelKey: t('Casino'),
      value: 1,
      id: 1,
      platformRespList: [],
      code: VENUE_CODE.LIVE
    },
    [VENUE_CODE.SLOT]: {
      labelKey: t('Slot'),
      value: 2,
      id: 2,
      platformRespList: [],
      code: VENUE_CODE.SLOT
    },
    [VENUE_CODE.SPORT]: {
      labelKey: t('Sport'),
      value: 4,
      id: 4,
      platformRespList: [],
      code: VENUE_CODE.SPORT
    },
    [VENUE_CODE.ESPORTS]: {
      labelKey: t('E-Sports'),
      value: 5,
      id: 5,
      code: VENUE_CODE.ESPORTS,
      platformRespList: []
    },
    [VENUE_CODE.FISH]: {
      labelKey: t('Fish'),
      value: 7,
      id: 7,
      code: VENUE_CODE.FISH,
      platformRespList: []
    }
  }))

  const entertainmentMap = computed<Record<string, (PlatformDTO | undefined)[]>>(() =>
    Object.fromEntries(
      gameCategoryData.value
        .filter((item) => item.code)
        .map((item) => [String(item.code).toUpperCase(), item.platformRespList ?? []])
    )
  )

  const toCategoryItem = (code: VenueCode): CategoryTabItem => {
    const config = CATEGORY_CONFIG.value[code]
    return {
      ...config,
      label: config.labelKey || config.label,
      platformRespList: (entertainmentMap.value[code] || []).filter(
        (item) => !item?.code?.includes('HB_')
      )
    }
  }

  const category = computed<CategoryTabItem[]>(() =>
    CATEGORY_ORDER.map((code) => toCategoryItem(code))
  )

  const venueCategory = computed<CategoryTabItem[]>(() =>
    VENUE_CATEGORY_CODES.map((code) => toCategoryItem(code))
  )

  const fetchGameCategories = async () => {
    const hasCache = gameCategoryData.value.length > 0
    const shouldShowLoading = !hasCache && !categoryHasRequested.value
    if (shouldShowLoading) {
      categoryLoading.value = true
    }

    try {
      const entertainmentResponse = await getEntertainment()
      gameCategoryData.value = entertainmentResponse.data || []
      return gameCategoryData.value
    } finally {
      categoryHasRequested.value = true
      categoryLoading.value = false
    }
  }

  return {
    category,
    venueCategory,
    categoryLoading,
    gameCategoryData,
    fetchGameCategories
  }
})
